<template>
  <div class="page-wrap">
    <div v-if="loaded">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="page-header__left">
          <button class="btn-back" @click="$router.back()">
            <i class="fa fa-arrow-left"></i>
          </button>
          <div>
            <h1 class="page-title">Promotion <span class="promo-code-title">{{ item.code }}</span></h1>
            <div class="page-meta">
              <span class="meta-item"><i class="fa fa-hashtag"></i> ID: {{ item.id }}</span>
              <span class="meta-item"><i class="fa fa-tag"></i> {{ item.type }}</span>
              <span class="meta-item"><i class="fa fa-percent"></i> {{ displayValue }}</span>
            </div>
          </div>
        </div>
        <div class="page-header__right">
          <span v-html="statusBadge"></span>
          <button class="action-btn action-btn--secondary" @click="$router.push({ name: 'promotion.edit', params: { id: item.id } })">
            <i class="fa fa-pencil"></i> Edit
          </button>
        </div>
      </div>

      <!-- ── Info Grid ── -->
      <div class="info-grid">
        <div class="info-card">
          <div class="info-card__label">Code</div>
          <div class="info-card__value mono">{{ item.code }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Type</div>
          <div class="info-card__value">{{ item.type }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Value</div>
          <div class="info-card__value">{{ displayValue }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Usage Limit</div>
          <div class="info-card__value">{{ item.limit_usage || '—' }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Times Used</div>
          <div class="info-card__value">{{ item.used_carts_count || 0 }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Start At</div>
          <div class="info-card__value date-val">{{ item.start_at || '—' }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Expire At</div>
          <div class="info-card__value date-val">{{ item.expire_at || '—' }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Status</div>
          <div class="info-card__value" v-html="statusBadge"></div>
        </div>
      </div>

      <!-- ── Products Panel ── -->
      <div class="panel" v-if="item.products && item.products.length > 0">
        <div class="panel-title"><i class="fa fa-cube"></i> Applicable Products</div>
        <div class="tags-wrap">
          <span class="product-tag" v-for="product in item.products" :key="product.id">
            {{ product.title }}
          </span>
        </div>
      </div>

      <!-- ── Carts Table ── -->
      <div class="panel" v-if="item.carts && item.carts.length > 0">
        <div class="panel-title"><i class="fa fa-shopping-cart"></i> Used by Customers ({{ item.carts.length }})</div>
        <div class="table-responsive-wrap">
          <table class="dd-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Discount Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cart in item.carts" :key="cart.id">
                <td class="cell-name">{{ cart.user.first_name }} {{ cart.user.last_name }}</td>
                <td class="cell-muted">{{ cart.user.email }}</td>
                <td class="cell-muted">{{ cart.user.phone_number || '—' }}</td>
                <td><span class="amount-val">{{ cart.promotion_amount }} AED</span></td>
                <td>
                  <span class="status-badge" :class="!cart.status ? 'status-badge--amber' : 'status-badge--gray'">
                    {{ !cart.status ? 'Used' : 'Unused' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="panel empty-panel" v-if="!item.carts || item.carts.length === 0">
        <i class="fa fa-shopping-cart"></i>
        <div>No customers have used this promotion yet.</div>
      </div>

    </div>

    <div class="mt-5 pt-5">
      <loading v-if="!loaded"></loading>
    </div>
  </div>
</template>

<script>
import { config } from "../helpers";

export default {
  name: "Promotion",
  data() {
    return {
      id: this.$route.params.id,
      loaded: false,
      item: null,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/promotions";
    this.loadData();
  },

  methods: {
    loadData() {
      axios.get(`${this.url}/${this.id}`).then((response) => {
        this.item = response.data.data;
        this.loaded = true;
      });
    },
  },

  computed: {
    displayValue() {
      if (!this.item) return '—';
      return this.item.type === 'Percent'
        ? `${(this.item.value * 100).toFixed(0)}%`
        : `${this.item.value} AED`;
    },

    statusBadge() {
      if (!this.item) return '';
      return this.item.is_active
        ? '<span class="status-badge status-badge--green">Active</span>'
        : '<span class="status-badge status-badge--red">Inactive</span>';
    },
  },
};
</script>

<style scoped>
.page-wrap { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }
.page-header__right { display: flex; align-items: center; gap: 10px; }
.btn-back { width: 34px; height: 34px; margin-top: 4px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: border-color .15s, color .15s; }
.btn-back:hover { border-color: var(--accent); color: var(--accent); }
.page-title { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 6px; }
.promo-code-title { font-family: monospace; color: var(--accent); letter-spacing: 1px; }
.page-meta { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
.meta-item { font-size: 12px; color: var(--text-3); display: flex; align-items: center; gap: 5px; }

/* ── Info Grid ── */
.info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.info-card { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1rem 1.1rem; }
.info-card__label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; }
.info-card__value { font-size: 14px; font-weight: 500; color: var(--text-1); }
.mono { font-family: monospace; font-size: 13px; letter-spacing: 1px; }
.date-val { font-size: 13px; }

/* ── Panel ── */
.panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.25rem; }
.panel-title { font-size: 12px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 1.1rem; display: flex; align-items: center; gap: 7px; }
.empty-panel { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 3rem 1rem; color: var(--text-3); font-size: 13px; }
.empty-panel i { font-size: 28px; }

/* ── Products ── */
.tags-wrap { display: flex; flex-wrap: wrap; gap: 8px; }
.product-tag { display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; background: rgba(99,179,237,.12); color: #63b3ed; border: 1px solid rgba(99,179,237,.25); }

/* ── Table ── */
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); text-align: left; white-space: nowrap; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table tr:last-child td { border-bottom: none; }
.cell-name { font-weight: 500; color: var(--text-1); }
.cell-muted { color: var(--text-3); font-size: 12px; }
.amount-val { font-weight: 600; color: var(--text-1); }

/* ── Badges ── */
.status-badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; }
.status-badge--green { background: rgba(34,197,94,.12);  color: #22c55e; border: 1px solid rgba(34,197,94,.3); }
.status-badge--red   { background: rgba(244,47,75,.12);  color: #f42f4b; border: 1px solid rgba(244,47,75,.3); }
.status-badge--amber { background: rgba(245,158,11,.12); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.status-badge--gray  { background: rgba(156,163,175,.12); color: #9ca3af; border: 1px solid rgba(156,163,175,.3); }

/* ── Action Buttons ── */
.action-btn { height: 36px; padding: 0 14px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: opacity .15s; border: 1px solid transparent; }
.action-btn--secondary { background: var(--bg-hover); color: var(--text-2); border-color: var(--border); }
.action-btn--secondary:hover { border-color: var(--border-2); color: var(--text-1); }

@media (max-width: 1024px) { .info-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px)  { .info-grid { grid-template-columns: 1fr 1fr; } }
</style>
